export {ManageSubscription as Page};

import {Helmet} from "react-helmet-async";
import {AccountSidebar} from "../../../components/AccountSidebar.jsx";
import messages from "../../../messages/messages.js";
import {useState, useEffect, useContext} from "preact/hooks";
import {NotificationContext} from '../../../components/NotificationContext.jsx';
import API_URL from "../../../includes/api.js";
import {useAuth} from "../../../components/AuthProvider.jsx";
import {ArrowDownTrayIcon, XMarkIcon, EyeIcon, InformationCircleIcon} from '@heroicons/react/24/solid';

function ManageSubscription() {
    const {addNotification} = useContext(NotificationContext);
    const {getToken, user, authLoading} = useAuth();

    const [loadingHistory, setLoadingHistory] = useState(false);
    const [loadingActive, setLoadingActive] = useState(false);
    const loading = loadingHistory || loadingActive;
    const [historyData, setHistoryData] = useState([]);
    const [activeSub, setActiveSub] = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(8);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        let found = false;
        if (params.get('success')) {
            addNotification('Payment was successful!', 'success');
            params.delete('success');
            found = true;
        }
        if (params.get('cancel')) {
            addNotification('Payment failed or was canceled.', 'error');
            params.delete('cancel');
            found = true;
        }
        if (found) {
            const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
            window.history.replaceState({}, '', newUrl);
        }
    }, [addNotification]);

    useEffect(() => {
        if (authLoading) return;

        if (!user) return;

        getToken().then(token => {
            loadBilling(token);
        }).catch(err => {
            addNotification(err.message || 'Authentication error', 'error');
        });
    }, [authLoading, user])

    const loadBilling = async (token) => {
        setLoadingHistory(true);
        setLoadingActive(true);
        try {
            const [historyRes, activeRes] = await Promise.all([
                fetch(API_URL + "/billing/history?firebase_auth_token=" + token).then(r => r.json()).catch(err => ({error: err})),
                fetch(API_URL + "/billing/active_subscription?firebase_auth_token=" + token).then(r => r.json()).catch(err => ({error: err}))
            ]);

            if (historyRes) {
                if (historyRes.code === 200) {
                    setHistoryData(historyRes.data);
                } else if (historyRes.error) {
                    addNotification(historyRes.error.message || 'Failed to fetch history', 'error');
                } else if (historyRes.message) {
                    addNotification(historyRes.message, 'error');
                }
            }

            if (activeRes) {
                if (activeRes.code === 200) {
                    setActiveSub(activeRes.data);
                } else if (activeRes.error) {
                    addNotification(activeRes.error.message || 'Failed to fetch active subscription', 'error');
                } else if (activeRes.message) {
                    addNotification(activeRes.message, 'error');
                }
            }
        } catch (e) {
            addNotification(e.message || 'Network error', 'error');
        } finally {
            setLoadingHistory(false);
            setLoadingActive(false);
        }
    };

    const cancelSubscription = async (subscriptionId) => {
        if (!subscriptionId) return addNotification('Missing subscription id', 'error');
        if (!confirm('Are you sure you want to cancel this subscription?')) return;
        setCanceling(true);
        try {
            const token = await getToken();
            const response = await fetch(API_URL + "/billing/cancel_subscription?subscription_id=" + subscriptionId + "&firebase_auth_token=" + token);
            const data = await response.json();
            if (data.code === 200) {
                addNotification('Subscription canceled successfully', 'success');
                await loadBilling(token);
            } else {
                addNotification(data.message || 'Failed to cancel subscription', 'error');
            }
        } catch (err) {
            addNotification(err.message || 'Network error', 'error');
        } finally {
            setCanceling(false);
        }
    };

    return (
        <section className="text-white">
            <Helmet>
                <title>{messages["SITE.NAME"]} | Manage Subscription</title>
                <meta name="description" content="Manage your subscription"/>
            </Helmet>

            <div>
                <div className="flex flex-wrap">
                    <AccountSidebar/>

                    <div className="w-full md:w-3/4 px-0 md:px-4">
                        <div className="md:border border-border/60 bg-zinc-800 rounded-lg md:p-6 mb-4">
                            <h2 className="text-xl font-semibold mb-2">Current Subscription</h2>
                            <p className="mb-2 text-zinc-300">View your current subscription, upgrade, downgrade, or
                                cancel your plan here.</p>
                            <div className="text-zinc-300">

                                {(!activeSub?.subscription || (Array.isArray(activeSub.subscription) ? activeSub.subscription.length === 0 : Object.keys(activeSub.subscription).length === 0)) ? (
                                    <div className="p-6 text-yellow-800 rounded-lg bg-yellow-50">
                                        <div className="flex items-center">
                                            <InformationCircleIcon className="w-5 h-5 mr-2"/>
                                            <h3 className="text-lg m-0 text-yellow-800 leading-none">No subscription</h3>

                                        </div>
                                        <div className="text-sm mt-2 mb-2">
                                            You do not have an active subscription. Become a subscriber to support us and unlock all content.
                                        </div>
                                        <a href='/subscribe'
                                           className="text-white bg-yellow-800 hover:bg-yellow-900 font-medium rounded-lg text-xs px-3 py-1.5 text-center inline-flex items-center">
                                            <EyeIcon className="w-4 h-4 mr-2"/>
                                            Subscribe
                                        </a>
                                    </div>
                                ) : (
                                    (() => {
                                        const created = activeSub?.created ? new Date(activeSub.created * 1000) : null;
                                        const periodEnd = activeSub?.current_period_end ? new Date(activeSub.current_period_end * 1000) : null;
                                        let formattedAmount;
                                        const planAmount = activeSub?.plan?.amount;
                                        const planCurrency = (activeSub?.plan?.currency || 'EUR').toUpperCase();
                                        if (typeof planAmount === 'number') {
                                            try {
                                                formattedAmount = new Intl.NumberFormat(undefined, {
                                                    style: 'currency',
                                                    currency: planCurrency
                                                }).format(planAmount);
                                            } catch (e) {
                                                formattedAmount = planCurrency + ' ' + planAmount;
                                            }
                                        } else if (planAmount != null) {
                                            formattedAmount = planCurrency + ' ' + planAmount;
                                        } else {
                                            formattedAmount = '-';
                                        }

                                        return (
                                            <div className="space-y-3">
                                                <div
                                                    className="bg-zinc-900/30 rounded flex flex-col md:flex-row md:items-center md:justify-between">
                                                    <div>
                                                        <div
                                                            className="text-zinc-400 text-sm">{formattedAmount} / {activeSub?.plan?.interval}</div>
                                                        <div className="text-zinc-400 text-sm mt-1">Status: <span
                                                            className="capitalize">{activeSub?.status}</span></div>
                                                        <div
                                                            className="text-zinc-400 text-sm">Started: {created ? created.toLocaleString() : '-'}</div>
                                                        <div className="text-zinc-400 text-sm">Period
                                                            ends: {periodEnd ? periodEnd.toLocaleString() : '—'}</div>
                                                    </div>
                                                    <div className="mt-3 md:mt-0">
                                                        {activeSub?.status === 'active' ? (
                                                            <button disabled={canceling}
                                                                    onClick={() => cancelSubscription(activeSub?.id)}
                                                                    className="p-2 cursor-pointer bg-red-700 hover:bg-red-600 text-white rounded inline-flex items-center"
                                                                    aria-label="Cancel subscription">
                                                                {canceling ? 'Canceling…' : (
                                                                    <XMarkIcon className="h-5 w-5" />
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <div className="text-zinc-400">Subscription
                                                                is {activeSub?.status}.</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                        </div>
                        <div className="md:border border-border/60 bg-zinc-800 rounded-lg md:p-6 mb-4">
                            <h2 className="text-xl font-semibold mb-2">Payment History & Invoices</h2>
                            <p className="mb-2 text-zinc-300">Your payment history and invoice downloads will appear
                                here.</p>
                            <div className="overflow-x-auto">
                                {loading ? (
                                    <div className="text-zinc-400">Loading history...</div>
                                ) : historyData.length === 0 ? (
                                    <div className="text-zinc-400">No payment history found.</div>
                                ) : (
                                    <>
                                        <table className="min-w-full text-left text-sm">
                                            <thead>
                                            <tr className="text-zinc-300 border-b border-border/40">
                                                <th className="py-2">Description</th>
                                                <th className="py-2 px-3">Date</th>
                                                <th className="py-2 px-3">Amount</th>
                                                <th className="py-2 px-3">Status</th>
                                                <th className="py-2 px-3">Invoice</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {historyData.slice((page - 1) * pageSize, page * pageSize).map(item => {
                                                const created = item.created ? new Date(item.created * 1000) : null;
                                                const amount = (item.amount_paid != null ? item.amount_paid : item.amount_due != null ? item.amount_due : 0);
                                                const description = item.description || (item.lines && item.lines.data && item.lines.data[0] && item.lines.data[0].description) || item.statement_descriptor || '-';
                                                let formatted;
                                                try {
                                                    formatted = new Intl.NumberFormat(undefined, {
                                                        style: 'currency',
                                                        currency: (item.currency || 'EUR').toUpperCase()
                                                    }).format(amount);
                                                } catch (e) {
                                                    formatted = (item.currency || '').toUpperCase() + ' ' + (typeof amount === 'number' && amount.toFixed ? amount.toFixed(2) : amount);
                                                }
                                                return (
                                                    <tr key={item.id} className="odd:bg-zinc-900/30">
                                                        <td className="py-2 text-zinc-200">{description}</td>
                                                        <td className="py-2 px-3 text-zinc-200">{created ? created.toLocaleString() : '-'}</td>
                                                        <td className="py-2 px-3 text-zinc-200">{formatted}</td>
                                                        <td className="py-2 px-3 text-zinc-200 capitalize">{item.status || '-'}</td>
                                                        <td className="py-2 px-3">
                                                            {item.hosted_invoice_url ? (
                                                                <a href={item.hosted_invoice_url} target="_blank"
                                                                   rel="noopener noreferrer"
                                                                   className="inline-flex items-center text-zinc-200 hover:text-white">
                                                                    <ArrowDownTrayIcon className="h-5 w-5 mr-1"/>
                                                                    Download
                                                                </a>
                                                            ) : (
                                                                <span className="text-zinc-500">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                            </tbody>
                                        </table>

                                        <div className="mt-4 flex items-center justify-between">
                                            <div
                                                className="text-zinc-400 text-sm">Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, historyData.length)} of {historyData.length}</div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    className="px-3 py-1 rounded bg-zinc-700 text-white disabled:opacity-50"
                                                    disabled={page === 1}
                                                    onClick={() => setPage(p => Math.max(1, p - 1))}>Prev
                                                </button>
                                                <div
                                                    className="text-zinc-300 text-sm">Page {page} / {Math.ceil(historyData.length / pageSize) || 1}</div>
                                                <button
                                                    className="px-3 py-1 rounded bg-zinc-700 text-white disabled:opacity-50"
                                                    disabled={page >= Math.ceil(historyData.length / pageSize)}
                                                    onClick={() => setPage(p => Math.min(Math.ceil(historyData.length / pageSize), p + 1))}>Next
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
